/**
 * Minimal cube-helper service for agent-homepage.
 * Local copy to avoid @loops-webv2/ui / apps/react-ui dependencies in prod build.
 */
import { getCubeHelperConfig } from './cube-helper-config';
import { COMPONENT } from './constants';

const parameters = { component: COMPONENT.HIERARCHICAL_QUERY };

let currentAgentUUID: string | null = null;
let workspaceTimezone: string | null = null;

const WORKSPACE_TIMEZONE_KEY = 'workspaceTimezone';

/** Sets the Cube Agent.id filter value (agentUserId from channel registration). */
export function setCurrentAgentUUID(agentUserId: string | null): void {
  currentAgentUUID = agentUserId;
}

export function getCurrentAgentUUID(): string | null {
  return currentAgentUUID;
}

/** Apply timezone from /teams/authorize when present; used for all cube queries and date formatting. */
export function setWorkspaceTimezone(timezone: string | null | undefined): void {
  if (!timezone) return;
  workspaceTimezone = timezone;
  try {
    localStorage.setItem(WORKSPACE_TIMEZONE_KEY, timezone);
    const app = JSON.parse(localStorage.getItem('app') || '{}');
    app.timezone = timezone;
    localStorage.setItem('app', JSON.stringify(app));
  } catch {
    // ignore storage errors
  }
}

function getCubeTimezone(): string {
  if (workspaceTimezone) return workspaceTimezone;
  try {
    const fromStorage = localStorage.getItem(WORKSPACE_TIMEZONE_KEY);
    if (fromStorage) return fromStorage;
    const app = JSON.parse(localStorage.getItem('app') || '{}');
    if (app?.timezone) return app.timezone;
  } catch {
    // ignore and fall back
  }
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC';
  } catch {
    return 'UTC';
  }
}

function getREST() {
  return getCubeHelperConfig().REST;
}

/** Add timezone to query for Cube API (workspace timezone from authorize, else browser). */
function injectTimeZoneInQuery(query: { timezone?: string } | null | undefined): void {
  if (query) {
    query.timezone = getCubeTimezone();
  }
}

/** Agent.id filter applied to all cube queries when an agent is selected */
const AGENT_FILTER = {
  member: 'Agent.id',
  operator: 'equals' as const,
  values: [] as string[],
};

function injectAgentFilter(query: Record<string, unknown>): Record<string, unknown> {
  if (!currentAgentUUID) return query;
  const q = { ...query };
  const existing = Array.isArray(q.filters) ? (q.filters as object[]) : [];
  q.filters = [...existing, { ...AGENT_FILTER, values: [currentAgentUUID] }];
  return q;
}

/** Dimension value pickers need global distinct values, not scoped to the current agent. */
type CubeQueryRunOptions = {
  skipAgentFilter?: boolean;
};

function prepareCubeQuery(
  query: Record<string, unknown>,
  options?: Pick<CubeQueryRunOptions, 'skipAgentFilter'>
): Record<string, unknown> {
  const q = options?.skipAgentFilter ? { ...query } : injectAgentFilter({ ...query });
  injectTimeZoneInQuery(q as { timezone?: string });
  return q;
}

export const cubeJsQuery = async (
  query: unknown,
  pageType?: string,
  options?: Pick<CubeQueryRunOptions, 'skipAgentFilter'>
): Promise<unknown> => {
  const q = prepareCubeQuery((query as Record<string, unknown>) ?? {}, options);
  return getREST().cubejsCal({ ...parameters, query: q, version: 'v2' }, pageType);
};

export const cubejsQueryWithoutCache = async (query: unknown): Promise<{ data: { rows: unknown[] } }> => {
  const q = prepareCubeQuery((query as Record<string, unknown>) ?? {});
  return getREST().cubejsCalWithoutCache(q);
};

export type CubeResultSetLike = {
  rawData: () => Record<string, unknown>[];
  query: () => Record<string, unknown>;
  annotation: () => { dimensions: Record<string, unknown>; measures: Record<string, unknown> };
};

const buildAnnotationForQuery = (query: Record<string, unknown>) => {
  const dimensions: Record<string, unknown> = {};
  const measures: Record<string, unknown> = {};

  const stubFor = (name: string) => ({
    shortTitle: name.split('.').pop() || name,
    title: name.split('.').pop() || name,
    type: name.toLowerCase().includes('time') ? 'time' : 'number',
  });

  const addMeasure = (name: string) => {
    if (!name || measures[name]) return;
    measures[name] = stubFor(name);
  };

  const addDimension = (name: string) => {
    if (!name || dimensions[name]) return;
    dimensions[name] = stubFor(name);
  };

  (query.measures as string[] | undefined)?.forEach(addMeasure);
  (query.dimensions as string[] | undefined)?.forEach(addDimension);
  (query.timeDimensions as Array<{ dimension?: string }> | undefined)?.forEach((td) => {
    if (td?.dimension) addDimension(td.dimension);
  });

  return { dimensions, measures };
};

/** Cache-bypass query with ResultSet-like API for charts and KPI helpers. */
export const cubejsQueryWithoutCacheUpdated = async (
  query: unknown,
  options?: Pick<CubeQueryRunOptions, 'skipAgentFilter'>
): Promise<CubeResultSetLike> => {
  const q = prepareCubeQuery((query as Record<string, unknown>) ?? {}, options);
  const res = await getREST().cubejsCalWithoutCache(q);
  const rows = (res?.data?.rows ?? []) as Record<string, unknown>[];
  const annotation = buildAnnotationForQuery(q);
  return {
    rawData: () => rows,
    query: () => q,
    annotation: () => annotation,
  };
};

/** Used by filter UI; matches main app cube meta visibility helper (no hidden-items config in agent-homepage). */
export const isHiddenCubeItem = (_item: unknown): boolean => false;

/**
 * Distinct values for a dimension (for filter value pickers).
 * Same pattern as apps/react-ui cubeJsHelperService.fetchDimensionValues.
 */
export const fetchDimensionValues = async (dimensionName: string): Promise<unknown[]> => {
  const query: Record<string, unknown> = {
    dimensions: [dimensionName],
    filters: [{ member: dimensionName, operator: 'notEquals', values: [null, '', ' '] }],
  };
  if (['Agent.name', 'Agent.email'].includes(dimensionName)) {
    query.segments = ['Agent.internal'];
    query.limit = 50000;
  }
  if (['Requester.name', 'Requester.email'].includes(dimensionName)) {
    query.limit = 50000;
  }
  if (['Author.name', 'Author.email'].includes(dimensionName)) {
    query.segments = ['Author.internal'];
    query.limit = 50000;
  }
  if (['ServiceIntegration.name'].includes(dimensionName)) {
    const filters = Array.isArray(query.filters) ? (query.filters as object[]) : [];
    if (!filters.some((f: any) => f.member === 'ServiceIntegration.isArchived')) {
      query.filters = [
        ...filters,
        { member: 'ServiceIntegration.isArchived', operator: 'notEquals', values: [1] },
      ];
    }
  }
  if (['Customer.name'].includes(dimensionName) || ['TicketCollaborator.name'].includes(dimensionName)) {
    query.limit = 50000;
  }
  const result = await cubeJsQuery(query, undefined, { skipAgentFilter: true });
  const raw = (result as { rawData?: () => Record<string, unknown>[] })?.rawData?.() ?? [];
  return raw;
};
